import { useState } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Building,
    AlertCircle,
    Download,
    Share,
    ChevronRight,
    Banknote,
    Calendar,
    FileText,
    Tag,
    HeartHandshake,
    TrendingUp,
    Users,
    Files,
    Scale,
    DollarSign,
    ClipboardCheck,
    Target,
    Mail,
    Folder,
    FileSpreadsheet,
    MessageSquare,
    Eye,
    MoreHorizontal,
    ChevronDown,
    ChevronUp,
    ArrowUpDown
} from 'lucide-react';
import DocumentThumbnail from './ThumbnailComponent';
import { SearchResultItem } from '@/types/search';
import { formatDate } from '@/lib/date';
import RupeeSign from './icons/RupeeSign';

export interface DocumentsTableProps {
  documents: SearchResultItem[];
  mode?: 'recent' | 'search' | 'filtered';
  loading?: boolean;
  onDocumentClick?: (id: string) => void;
}

export default function DocumentsTable({
  documents,
  mode = 'recent',
  loading = false,
  onDocumentClick,
}: DocumentsTableProps) {
    const [sortConfig, setSortConfig] = useState({field: '', direction: 'asc' as 'asc' | 'desc'});

    // Format currency for display
    const formatCurrency = (value?: number, currency?: string) => {
        if (value === undefined || currency === undefined) return null;

        try {
            return new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency: currency || 'INR',
                maximumFractionDigits: 0
            }).format(value);
        } catch (e) {
            return `${currency || 'INR'} ${value.toLocaleString('en-IN')}`;
        }
    };

    // Get tag color based on tag type
    const getTagColorClasses = (tagType: string) => {
        switch(tagType?.toLowerCase()) {
            case 'content':
                return "bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-100";
            case 'category':
                return "bg-purple-50 text-purple-700 hover:bg-purple-100 border-purple-100";
            case 'asset':
                return "bg-orange-50 text-orange-700 hover:bg-orange-100 border-orange-100";
            case 'type':
                return "bg-teal-50 text-teal-700 hover:bg-teal-100 border-teal-100";
            case 'purpose':
                return "bg-red-50 text-red-700 hover:bg-red-100 border-red-100";
            case 'subtopic':
                return "bg-green-50 text-green-700 hover:bg-green-100 border-green-100";
            case 'location':
            case 'mentioned location':
                return "bg-cyan-50 text-cyan-700 hover:bg-cyan-100 border-cyan-100";
            case 'sector':
            case 'industry sector':
                return "bg-amber-50 text-amber-700 hover:bg-amber-100 border-amber-100";
            case 'topic':
            case 'primary topic':
                return "bg-lime-50 text-lime-700 hover:bg-lime-100 border-lime-100";
            case 'person':
            case 'mention person':
                return "bg-pink-50 text-pink-700 hover:bg-pink-100 border-pink-100";
            case 'org':
            case 'mention organization':
                return "bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border-indigo-100";
            case 'theme':
                return "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-100";
            default:
                return "bg-gray-50 text-gray-700 hover:bg-gray-100 border-gray-100";
        }
    };

    // Get icon for category
    const getCategoryIcon = (category?: string) => {
        if (!category) return <Files className="w-3.5 h-3.5 mr-1 opacity-70" />;

        switch(category.toLowerCase()) {
            case 'grantee':
                return <HeartHandshake className="w-3.5 h-3.5 mr-1 opacity-70" />;
            case 'investment':
                return <TrendingUp className="w-3.5 h-3.5 mr-1 opacity-70" />;
            case 'internal':
                return <Users className="w-3.5 h-3.5 mr-1 opacity-70" />;
            default:
                return <Files className="w-3.5 h-3.5 mr-1 opacity-70" />;
        }
    };

    // Get icon for content category
    const getContentCategoryIcon = (contentCategory?: string) => {
        if (!contentCategory) return <Folder className="w-3.5 h-3.5 mr-1 opacity-70" />;

        switch(contentCategory.toLowerCase()) {
            case 'legal':
                return <Scale className="w-3.5 h-3.5 mr-1 opacity-70" />;
            case 'financial':
                return <RupeeSign className="w-3.5 h-3.5 mr-1 opacity-70" />;
            case 'administrative':
                return <ClipboardCheck className="w-3.5 h-3.5 mr-1 opacity-70" />;
            case 'program':
                return <Target className="w-3.5 h-3.5 mr-1 opacity-70" />;
            case 'communication':
                return <MessageSquare className="w-3.5 h-3.5 mr-1 opacity-70" />;
            default:
                return <Folder className="w-3.5 h-3.5 mr-1 opacity-70" />;
        }
    };

    // Render document card with financial data
    const renderDocumentCard = (doc: SearchResultItem) => {
        // Check for financial data (directly use the financial_data object)
        const primaryFinancial = doc.financial_data &&
            doc.financial_data.primary_figure === true &&
            doc.financial_data.from_antz === true ?
            doc.financial_data : null;

        return (
            <Card
                key={doc.document_id}
                className="hover:shadow-md transition-all cursor-pointer"
                onClick={() => onDocumentClick?.(doc.document_id)}
            >
                <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                        {/* Thumbnail */}
                        <DocumentThumbnail
                            thumbnail_url={doc.thumbnail_url}
                            title={doc.title}
                            hasThumbnail={!!doc.thumbnail_url}
                            category={doc.category}
                            fileType={doc.filetype}
                            size="lg"
                        />

                        <div className="flex-1 min-w-0">
                            {/* Title and Actions */}
                            <div className="flex items-start justify-between">
                                <div className="space-y-1">
                                    <h3 className="font-medium text-gray-900">{doc.title}</h3>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            // Handle download
                                        }}
                                    >
                                        <Download className="h-4 w-4"/>
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            // Handle share
                                        }}
                                    >
                                        <Share className="h-4 w-4"/>
                                    </Button>
                                </div>
                            </div>

                            {/* Breadcrumb metadata section */}
                            <div className="mt-2 space-y-2">
                                <div className="flex items-center flex-wrap gap-y-1 text-sm text-gray-500">
                                    {/* Organization */}
                                    {doc.organization && (
                                        <span className="flex items-center">
                                            <Building className="w-4 h-4 mr-1"/>
                                            {doc.organization}
                                        </span>
                                    )}

                                    {/* Date */}
                                    {doc.date && (
                                        <>
                                            <ChevronRight className="w-4 h-4 mx-2"/>
                                            <span className="flex items-center">
                                                <Calendar className="w-3.5 h-3.5 mr-1 opacity-70"/>
                                                {formatDate(doc.date)}
                                            </span>
                                        </>
                                    )}

                                    {/* Category - with icon */}
                                    {doc.category && (
                                        <>
                                            <ChevronRight className="w-4 h-4 mx-2"/>
                                            <span className="flex items-center capitalize">
                                                {getCategoryIcon(doc.category)}
                                                {doc.category}
                                            </span>
                                        </>
                                    )}

                                    {/* Content Category - with icon */}
                                    {doc.classification?.content?.content_category && (
                                        <>
                                            <ChevronRight className="w-4 h-4 mx-2"/>
                                            <span className="flex items-center capitalize">
                                                {getContentCategoryIcon(doc.classification.content.content_category)}
                                                {doc.classification.content.content_category}
                                            </span>
                                        </>
                                    )}

                                    {/* Content Type */}
                                    {doc.classification?.content?.content_type && (
                                        <>
                                            <ChevronRight className="w-4 h-4 mx-2"/>
                                            <span className="capitalize flex items-center">
                                                <FileText className="w-3.5 h-3.5 mr-1 opacity-70"/>
                                                {doc.classification.content.content_type}
                                            </span>
                                        </>
                                    )}

                                    {/* Financial Data - Primary figure from Antz */}
                                    {primaryFinancial && (
                                        <>
                                            <ChevronRight className="w-4 h-4 mx-2"/>
                                            <span className="capitalize flex items-center">
                                                <Banknote className="w-3.5 h-3.5 mr-1 opacity-70"/>
                                                {primaryFinancial.type || 'Financial'}
                                            </span>
                                            <ChevronRight className="w-4 h-4 mx-2"/>
                                            <span className="font-medium text-green-700">
                                                {formatCurrency(primaryFinancial.value, primaryFinancial.currency)}
                                            </span>
                                        </>
                                    )}
                                </div>

                                {/* Document summary/snippet */}
                                <div className="mt-1">
                                    {(doc.snippet || doc.summary) && (
                                        <p className="text-sm text-gray-600 line-clamp-2">
                                            {doc.snippet || doc.summary}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Tags section - Updated to handle Tag objects */}
                            {Array.isArray(doc.tags) && doc.tags.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-1.5">
                                    {doc.tags.map((tag, idx) => {
                                        // Handle tag objects with our expected structure
                                        const tagName = tag.name || '';
                                        const tagType = tag.type || 'default';

                                        // Only display tags with a name
                                        if (!tagName) return null;

                                        return (
                                            <Badge
                                                key={`${tagName}-${idx}`}
                                                variant="secondary"
                                                className={`px-2 py-0.5 border ${getTagColorClasses(tagType)}`}
                                            >
                                                <Tag className="h-3 w-3 mr-1" />
                                                {tagName}
                                            </Badge>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (documents.length === 0) {
        return (
            <Card>
                <CardContent className="flex flex-col items-center justify-center h-64">
                    <AlertCircle className="h-8 w-8 text-gray-400 mb-4"/>
                    <p className="text-gray-500">
                        {mode === 'search'
                            ? 'No documents found matching your search'
                            : 'No documents available'}
                    </p>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-4">
            {documents.map(doc => renderDocumentCard(doc))}
        </div>
    );
}